import { useCallback, useEffect, useState } from "react";

import {
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useSearchParams } from "react-router-dom";

import { IBrand } from "../../models/Brand/Brand";
import Form from "../../components/base/Form/Form";
import WilmaLogo from "../../assets/logo.svg";
import useApiHelper from "../../hooks/useApiHelper";
import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import { useTheme } from "../../contexts/ThemeContext";
import { BrandRequest } from "../../models/auth/BrandRequest";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { FormErrors } from "../../components/base/Form/FormErrors";
import { FormTextField } from "../../components/base/Form/FormTextField";
import { DefaultResponseWithData } from "../../models/system/DefaultResponse";
import {
  LoginRequestWithApplicationId,
  LoginRequestWithApplicationRegistration,
} from "../../models/auth/LoginRequest";
import { LoginResponse } from "../../models/auth/LoginResponse";
import { Separator } from "../../components/shared/Seperator";
import { authApiUrl, authApiVersion } from "../../config";

interface LoginFormFields {
  username: string;
  password: string;
}

type LoginMethod = "entra" | "password";

export default function Login() {
  const { post } = useApiHelper();
  const [urlParams] = useSearchParams();
  const { setTheme } = useTheme();

  //! Remember the last account per application so multi-app SSO keeps separate usernames
  const storageScope =
    urlParams.get("ari") ?? urlParams.get("app") ?? "default";
  const lastUsernameLsKey = `last-username:${storageScope}`;
  const lastMethodLsKey = `last-login-method:${storageScope}`;

  const [rememberedUsername, setRememberedUsername] = useState<string | undefined>(
    () => localStorage.getItem(lastUsernameLsKey) ?? undefined
  );
  const [rememberedMethod, setRememberedMethod] = useState<
    LoginMethod | undefined
  >(() => (localStorage.getItem(lastMethodLsKey) as LoginMethod) ?? undefined);
  const [useDifferentAccount, setUseDifferentAccount] = useState(false);
  const [accountSelected, setAccountSelected] = useState(false);
  const [usernameEnabled, setUsernameEnabled] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signUpVisible, setSignUpVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string>();
  const [ari, setAri] = useState<string>();
  const [app, setApp] = useState<string>();
  const [entraIdEnabled, setEntraIdEnabled] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const stopTailwindStrippingClasses = "bg-gray-50 h-full";

  const rememberUsername = (username: string | undefined, method: LoginMethod) => {
    if (!username) return;

    localStorage.setItem(lastUsernameLsKey, username);
    localStorage.setItem(lastMethodLsKey, method);
    setRememberedUsername(username);
    setRememberedMethod(method);
  };

  const forgetUsername = () => {
    localStorage.removeItem(lastUsernameLsKey);
    localStorage.removeItem(lastMethodLsKey);
    setRememberedUsername(undefined);
    setRememberedMethod(undefined);
    setUseDifferentAccount(true);
  };

  const handleLogin = (data: LoginFormFields) => {
    setShowPassword(false);

    if (ari) {
      loginWithApplicationRegistration(data);
    } else {
      loginWithApplicationId(data);
    }
  };

  const loginWithApplicationId = async (data: LoginFormFields) => {
    setLoading(true);

    try {
      const loginResponse = await post<
        LoginRequestWithApplicationId,
        DefaultResponseWithData<LoginResponse>
      >("/auth/login", { ...data, applicationId: app! });

      rememberUsername(data.username, "password");

      if (urlParams.has("returnUrl")) {
        window.location.assign(
          `${urlParams.get("returnUrl")!}?ari=${
            loginResponse.data.applicationRegistrationId
          }`
        );
      } else {
        setAuthError("No return URL found!");
      }
    } catch (e: any) {
      if (e?.status) {
        setAuthError(e.message);
      } else {
        setAuthError("Unexpected error, please contact support");
      }
    } finally {
      //! Timeout to stop flicker when redirecting
      setTimeout(() => setLoading(false), 200);
    }
  };

  const loginWithApplicationRegistration = async (data: LoginFormFields) => {
    setLoading(true);

    try {
      await post<
        LoginRequestWithApplicationRegistration,
        DefaultResponseWithData<LoginResponse>
      >("/auth/login", { ...data, applicationRegistrationId: ari! });

      rememberUsername(data.username, "password");

      if (urlParams.has("returnUrl")) {
        window.location.assign(urlParams.get("returnUrl")!);
      } else {
        setAuthError("No return URL found!");
      }
    } catch (e: any) {
      if (e?.status) {
        setAuthError(e.message);
      } else {
        setAuthError("Unexpected error, please contact support");
      }
    } finally {
      //! Timeout to stop flicker when redirecting
      setTimeout(() => setLoading(false), 200);
    }
  };

  const redirectToEntra = useCallback((username?: string) => {
    const params = new URLSearchParams();

    const currentAri = urlParams.get("ari");
    const currentApp = urlParams.get("app");

    if (currentAri) params.set("ari", currentAri);
    else if (currentApp) params.set("app", currentApp);

    if (username) {
      params.set("username", username);
      localStorage.setItem(lastUsernameLsKey, username);
      localStorage.setItem(lastMethodLsKey, "entra");
    }

    if (urlParams.has("returnUrl"))
      params.set("returnUrl", urlParams.get("returnUrl")!);

    window.location.assign(
      `${authApiUrl}/${authApiVersion}/auth/entra/authorize?${params.toString()}`
    );
  }, [urlParams, lastUsernameLsKey, lastMethodLsKey]);

  const getBrand = useCallback(
    async (
      username?: string,
      applicationRegistrationId?: string,
      applicationId?: string
    ) => {
      setLoading(true);
      authError && setAuthError(undefined);

      try {
        const emailResult = await post<
          BrandRequest,
          DefaultResponseWithData<IBrand>
        >("/brand/lookup", {
          username,
          applicationRegistrationId,
          applicationId,
        });

        if (emailResult.success) {
          setTheme(emailResult.data);

          //! Sign up is only offered when we know which application is being signed up for
          //! (landing with ?app=), and that application allows it. Set before any early return below.
          setSignUpVisible(
            urlParams.has("app") && emailResult.data.allowSignup === true
          );

          if (emailResult.data.entraIdEnabled) {
            if (emailResult.data.entraIdAutoRedirect) {
              redirectToEntra(username);
              return;
            }

            if (emailResult.data.userSsoLogin && username) {
              redirectToEntra(username);
              return;
            }

            setEntraIdEnabled(true);

            if (!username) {
              return;
            }
          }

          if (!username && emailResult.data.isDefault) {
            return;
          }

          setUsernameEnabled(true);
          setPasswordVisible(true);
        } else {
          setAuthError(emailResult.message);
        }
      } catch (e) {
        setAuthError("Unexpected error, please contact support");
      } finally {
        setLoading(false);
      }
    },
    [authError, post, setTheme, redirectToEntra, urlParams]
  );

  useEffect(() => {
    if (urlParams.has("error")) {
      setAuthError(
        urlParams.get("message") || "Authentication failed"
      );
    }

    if (urlParams.has("ari")) {
      const ari = urlParams.get("ari")!;

      setAri(ari);
      getBrand(undefined, ari);
    } else if (urlParams.has("app")) {
      const app = urlParams.get("app")!;

      setApp(app);
      getBrand(undefined, undefined, app);
    } else {
      setLoading(false);
    }
  }, []);

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:pt-16 sm:px-6 lg:px-8">
      <div className="mb-12 flex justify-center sm:mb-16 sm:mx-auto sm:w-full sm:max-w-md">
        <img className="h-14" src={WilmaLogo} alt="Logo" />
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="bg-white px-6 py-12 shadow prose sm:rounded-lg sm:px-12">
          <h2>Sign in</h2>
          <Form<LoginFormFields> onSubmit={handleLogin} className="space-y-4">
            {({ register, watch, setValue }) => {
              const showAccountChooser =
                !!rememberedUsername && !useDifferentAccount && !accountSelected;

              return loading ? (
                <LoadingWheel />
              ) : (
                <>
                  {authError && (
                    <Alert
                      severity="error"
                      title="Login Error"
                      message={authError}
                    />
                  )}
                  <FormErrors />
                  {showAccountChooser ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          if (rememberedMethod === "entra") {
                            redirectToEntra(rememberedUsername);
                            return;
                          }

                          setAccountSelected(true);
                          setValue("username", rememberedUsername!);
                          getBrand(rememberedUsername);
                        }}
                        className="flex w-full items-center gap-4 rounded-lg border border-gray-300 p-4 text-left transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <span className="flex-1 truncate text-sm font-medium text-gray-900">
                          {rememberedUsername}
                        </span>
                        <span
                          role="button"
                          aria-label="Forget this account"
                          className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            forgetUsername();
                          }}
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </span>
                      </button>
                      <div className="!mt-8">
                        <p className="!mb-1 font-semibold">Not you?</p>
                        <button
                          type="button"
                          onClick={() => setUseDifferentAccount(true)}
                          className="text-primary underline hover:no-underline focus:outline-none"
                        >
                          Login with a different account
                        </button>
                      </div>
                    </>
                  ) : (
                    <FormTextField
                      disabled={!usernameEnabled}
                      label="Username"
                      fullWidth
                      {...register("username", { required: true })}
                    />
                  )}
                  {!passwordVisible && !showAccountChooser && (
                    <Button
                      type="button"
                      className="!mt-10"
                      onClick={(e) => {
                        e.preventDefault();
                        getBrand(watch("username"));
                      }}
                      fullWidth
                    >
                      Next
                    </Button>
                  )}
                  {passwordVisible && !showAccountChooser && (
                    <>
                      <FormTextField
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        fullWidth
                        endAdornment={
                          <button
                            type="button"
                            className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() =>
                              setShowPassword((current: boolean) => !current)
                            }
                          >
                            {showPassword ? (
                              <EyeSlashIcon className="h-5 w-5" />
                            ) : (
                              <EyeIcon className="h-5 w-5" />
                            )}
                          </button>
                        }
                        {...register("password", { required: true })}
                      />
                      <Button type="submit" className="!mt-10" fullWidth>
                        Login
                      </Button>
                      <Button variant="outlined" fullWidth onClick={(e) => {
                        e.preventDefault();
                        if (urlParams.has("returnUrl")) {
                          let query = "returnUrl=" + urlParams.get("returnUrl");
                          if (urlParams.has("ari")) query +=  "&ari=" + urlParams.get("ari");
                          else if (urlParams.has("app")) query +=  "&app=" + urlParams.get("app");
                          
                          window.location.assign("/password-reset?" + query);
                        } else {
                          window.location.assign("/password-reset");
                        }
                      }}>
                        Forgotten Password?
                      </Button>
                    </>
                  )}
                  {entraIdEnabled && (
                    <>
                      <Separator />
                      <Button
                        type="button"
                        variant="outlined"
                        fullWidth
                        onClick={(e) => {
                          e.preventDefault();
                          redirectToEntra(watch("username"));
                        }}
                      >
                        Login with Microsoft
                      </Button>
                    </>
                  )}
                  {signUpVisible && !showAccountChooser && (
                    <>
                      <Separator />
                      <Button variant="outlined" fullWidth onClick={(e) => {
                        e.preventDefault();
                        const query = new URLSearchParams();
                        if (urlParams.has("app")) query.set("app", urlParams.get("app")!);
                        if (urlParams.has("returnUrl")) query.set("returnUrl", urlParams.get("returnUrl")!);
                        window.location.assign("/register?" + query.toString());
                      }}>
                        Sign Up
                      </Button>
                    </>
                  )}
                </>
              );
            }}
          </Form>
        </div>
      </div>
    </div>
  );
}
